import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CONTACT_EMAIL } from "../pages/Legal";
import Navbar from "./Navbar";
import MiniPlayer, { SteelFace, SteelDefs } from "./MiniPlayer";
import SearchPopup from "./SearchPopup";
import SuggestionBox, { openSuggestionBox } from "./SuggestionBox";
import SongRequestBox, { openSongRequestBox } from "./SongRequestBox";
import NewsletterSignup from "./NewsletterSignup";
import PageStats from "./PageStats";
import { Toaster, toast } from "sonner";
import { usePlayer } from "../context/PlayerContext";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

// Shown above every page to someone who has not confirmed their email yet (only when the site asks for that).
function VerifyBanner() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  if (!user || user.email_verified !== false) return null;
  const again = async () => {
    setBusy(true);
    try { await api.post("/auth/resend-verification"); toast.success("Sent. Check your inbox, and your spam folder too."); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send the email. Please try again."); }
    finally { setBusy(false); }
  };
  return (
    <div data-testid="verify-banner" className="mb-4 rounded-xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 text-sm flex flex-wrap items-center gap-x-4 gap-y-2">
      <span className="min-w-0">Please confirm your email address. We sent a link to <b className="break-all">{user.email}</b>.</span>
      <button type="button" onClick={again} disabled={busy} data-testid="verify-resend"
        className="px-3 py-1.5 rounded-full border border-amber-400/50 text-amber-200 hover:bg-amber-400/10 disabled:opacity-60">{busy ? "Sending…" : "Send the link again"}</button>
    </div>
  );
}

export default function Layout({ children }) {
  const { current } = usePlayer();
  const { user } = useAuth();
  const [searchOpen, setSearchOpen] = useState(false);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  const closeSearch = useCallback(() => setSearchOpen(false), []);
  const { pathname, search } = useLocation();

  // The search bar shown at the top of the pages where people browse songs (phones only, md:hidden).
  // It opens the same search popup as the buttons. The log in, account and legal pages do not need it.
  const showPhoneBar = pathname === "/" || /^\/(music|videos|genres|search|favorites|recent|following|playlists|watch)(\/|$)/.test(pathname);

  // Going to another page (including the browser Back button) closes the popup.
  useEffect(() => { setSearchOpen(false); }, [pathname, search]);

  // Press "/" anywhere (when not typing in a box) to open search.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const tag = t && t.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || (t && t.isContentEditable)) return;
      e.preventDefault();
      setSearchOpen(true);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div className="min-h-screen">
      <SteelDefs />
      <PageStats />
      <Navbar onSearch={openSearch} />
      <main className={`pt-20 ${current ? "pb-40 lg:pb-32" : "pb-24 lg:pb-8"} max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-10`}>
        {showPhoneBar && (
          <button type="button" onClick={openSearch} aria-label="Search songs" data-testid="phone-search-bar"
            className="md:hidden w-full flex items-center gap-3 h-12 pl-1.5 pr-4 mb-4 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-400 text-left active:bg-white/10">
            <span className="steel-btn w-9 h-9 shrink-0"><SteelFace glyph="search" /></span>
            <span className="truncate">Search songs, artists, moods, genres…</span>
          </button>
        )}
        <VerifyBanner />
        {children}
        <footer data-testid="site-footer" className="mt-16 pt-6 border-t border-white/10 text-xs text-zinc-500">
          <NewsletterSignup />
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
          <span>© {new Date().getFullYear()} Dan The Man TV</span>
          <Link to="/about" data-testid="footer-about" className="hover:text-zinc-300">About</Link>
          <Link to="/terms" data-testid="footer-terms" className="hover:text-zinc-300">Terms</Link>
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-zinc-300">Privacy</Link>
          <button type="button" onClick={openSongRequestBox} data-testid="footer-song-request" className="hover:text-zinc-300">Request a song</button>
          <button type="button" onClick={openSuggestionBox} data-testid="footer-suggest" className="hover:text-zinc-300">Send Dan a suggestion</button>
          <a href={`mailto:${CONTACT_EMAIL}`} data-testid="footer-contact" className="hover:text-zinc-300">Contact</a>
          {user && <Link to="/account" data-testid="footer-email-settings" className="hover:text-zinc-300">Email settings</Link>}
          </div>
        </footer>
      </main>
      <SuggestionBox />
      <SongRequestBox />
      <MiniPlayer />
      {/* Phones and tablets: a search button at the bottom right, in thumb reach.
          It sits above the mini player when a song is playing. */}
      {!searchOpen && (
        <div className="lg:hidden fixed right-4 z-40"
          style={{ bottom: `calc(${current ? 96 : 20}px + env(safe-area-inset-bottom, 0px))` }}>
          <button type="button" onClick={openSearch} aria-label="Search" data-testid="search-fab"
            className="steel-btn steel-btn--primary w-12 h-12">
            <SteelFace glyph="search" bevel />
          </button>
        </div>
      )}
      <SearchPopup open={searchOpen} onClose={closeSearch} />
      <Toaster theme="dark" position="bottom-right" richColors />
    </div>
  );
}
